import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface IncidentRow {
  readonly incidentId: string;
  readonly incidentCode: string;
  readonly title: string;
  readonly incidentCategory: string;
  readonly severity: string;
  readonly priority: string;
  readonly status: string;
  readonly source: string;
  readonly slaBreached: boolean;
}

const SEVERITIES = ['', 'CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];
const STATUSES = ['', 'REPORTED', 'CLASSIFIED', 'ASSIGNED', 'INVESTIGATING', 'CONTAINED', 'RESOLVED', 'CLOSED', 'POST_REVIEW'];
const CREATE_SEVERITIES = SEVERITIES.filter(Boolean);

/**
 * M30 — Incident Management (§14 / §31). Lists security incidents with their
 * category, severity, workflow status, origin and SLA-breach flag so the SOC
 * can triage, contain and close per the incident-response lifecycle. Every
 * incident is auditable (BR-02) and can be escalated to executive review.
 * The SOC can log a new incident and, per row, close or escalate it — each
 * wired to the guarded /v1/security/incidents write API.
 */
export function M30IncidentManagementPage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [rows, setRows] = useState<IncidentRow[]>([]);
  const [severity, setSeverity] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  // create form
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('');
  const [newSeverity, setNewSeverity] = useState('HIGH');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (severity) params.set('severity', severity);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: IncidentRow[] }>(`/v1/security/incidents${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, severity, status, reload]);

  const failMessage = (err: unknown, verb: string, perm: string): string =>
    err instanceof ApiError && err.status === 403
      ? t('m30.incident.notAuthorised', { verb, perm })
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!title.trim()) { setActionError(t('m30.incident.titleRequired')); return; }
    setBusy(true);
    try {
      const r = await apiRequest<{ data: IncidentRow }>(`/v1/security/incidents`, {
        method: 'POST',
        body: {
          title: title.trim(),
          incidentCategory: category.trim() || undefined,
          severity: newSeverity,
        },
        token,
      });
      setNotice(t('m30.incident.logged', { code: r.data.incidentCode }));
      setTitle('');
      setCategory('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, t('m30.incident.verbLog'), 'M30_INCIDENT_MANAGE'));
    } finally {
      setBusy(false);
    }
  };

  const runRowAction = async (
    fn: () => Promise<unknown>,
    successMsg: string,
    verb: string,
    perm: string,
  ): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(successMsg);
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err, verb, perm));
    }
  };

  const close = (i: IncidentRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/incidents/${i.incidentId}/close`, { method: 'POST', body: {}, token }),
      t('m30.incident.closed', { code: i.incidentCode }), t('m30.incident.verbClose'), 'M30_INCIDENT_MANAGE',
    );

  const escalate = (i: IncidentRow): Promise<void> =>
    runRowAction(
      () => apiRequest(`/v1/security/incidents/${i.incidentId}/escalate`, { method: 'POST', body: {}, token }),
      t('m30.incident.escalated', { code: i.incidentCode }), t('m30.incident.verbEscalate'), 'M30_INCIDENT_MANAGE',
    );

  return (
    <main aria-label="incident-management">
      <h1>{t('m30.incident.title')}</h1>

      <section aria-label="create-incident">
        <h2>{t('m30.incident.logHeading')}</h2>
        <form aria-label="incident-form" onSubmit={(e) => void create(e)}>
          <label>{t('m30.incident.fieldTitle')}<input aria-label="field-incident-title" value={title} onChange={(e) => setTitle(e.target.value)} /></label>
          <label>{t('m30.incident.fieldCategory')}<input aria-label="field-incident-category" value={category} onChange={(e) => setCategory(e.target.value)} /></label>
          <label>{t('m30.incident.fieldSeverity')}
            <select aria-label="field-incident-severity" value={newSeverity} onChange={(e) => setNewSeverity(e.target.value)}>
              {CREATE_SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-incident" disabled={busy}>{busy ? t('m30.incident.submitLogging') : t('m30.incident.submitLog')}</button>
        </form>
        {actionError && <p role="alert" aria-label="incident-error">{actionError}</p>}
        {notice && <p aria-label="incident-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {t('m30.incident.filterSeverity')}
          <select aria-label="filter-severity" value={severity} onChange={(e) => setSeverity(e.target.value)}>
            {SEVERITIES.map((s) => <option key={s || 'all'} value={s}>{s || t('m30.incident.all')}</option>)}
          </select>
        </label>
        <label>
          {t('m30.incident.filterStatus')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || t('m30.incident.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{t('m30.incident.loading')}</p> : (
        <>
          <p aria-label="incident-count">{t('m30.incident.count', { count: rows.length })}</p>
          <table aria-label="incident-table">
            <thead><tr><th>{t('m30.incident.thCode')}</th><th>{t('m30.incident.thTitle')}</th><th>{t('m30.incident.thCategory')}</th><th>{t('m30.incident.thSeverity')}</th><th>{t('m30.incident.thStatus')}</th><th>{t('m30.incident.thSource')}</th><th>{t('m30.incident.thSla')}</th><th>{t('m30.incident.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((i) => (
                <tr key={i.incidentId}>
                  <td>{i.incidentCode}</td><td>{i.title}</td><td>{i.incidentCategory}</td>
                  <td>{i.severity}</td><td>{i.status}</td><td>{i.source}</td>
                  <td>{i.slaBreached ? t('m30.incident.slaBreached') : t('m30.incident.slaOk')}</td>
                  <td>
                    <button type="button" aria-label={`escalate-${i.incidentCode}`} onClick={() => void escalate(i)}>{t('m30.incident.btnEscalate')}</button>
                    <button type="button" aria-label={`close-${i.incidentCode}`} onClick={() => void close(i)}>{t('m30.incident.btnClose')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
